import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Card, Container, Step, StepButton, Stepper, Typography } from '@material-ui/core';
import { Loader, Modal, SGSButton, SGSForm } from 'components';
import { useAppContext } from 'core/contexts';
import { TypeMode, TypeRole, TypeTitre, UserFormData, Utilisateur, formStepListByMode, initUtilisateur, initUtilisateurExample } from 'core/models';
import { addServerErrors } from 'core/utils/formValidation';
import { isVisitorBackOffice } from 'core/utils/roles';
import { FormDroits } from 'features/utilisateurs/components/form/formDroits';
import { FormInfos } from 'features/utilisateurs/components/form/formInfos';
import { FormPerimetre } from 'features/utilisateurs/components/form/formPerimetre';
import { useUserInfosContext } from 'features/utilisateurs/contexts/UtilisateurInfosProvider';
import { delay } from 'lodash';
import React, { useEffect, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import accountCreated from '../../../assets/account_created.svg';
import btnReconnect from '../../../assets/btnReconnect.svg';
import checkGreen from '../../../assets/checkGreen.svg';
import reconnect from '../../../assets/reconnect.svg';
import { utilisateurService } from '../../../core/services';
import { useUserDroitsContext } from '../contexts/UtilisateurDroitsProvider';
import { useUtilisateurFromContext } from '../contexts/UtilisateurFormProvider';
import { useUserPerimetreContext } from '../contexts/UtilisateurPerimetreProvider';
import { responsablesFromUsers } from '../utils/common';
import styles from './Utilisateurs.module.scss';
import { FormAlerte } from './form/formAlerte';
import { FormCodeGestionnaire } from './form/formCodeGestionnaire';
import { FormValidation } from './form/validationSchema';

type Props = { mode?: TypeMode };

const UserPageAdd: React.FC<Props> = ({ mode }) => {
    const { t } = useTranslation();
    const { user } = useAppContext();
    const {} = useUserDroitsContext();
    const { selectedUser, setSelectedUser, isFormSubmitted, setIsFormSubmitted, isFormSuccess, setIsFormSuccess, setUserConnected, isCheckingLogin, isCheckingEmail } = useUtilisateurFromContext();
    const { setLoadingPerimetre, setHasPerimetreErreur, setPerimetre, setSante, setPrevoyance, setSelectedColleges, setSelectedSouscrips } = useUserPerimetreContext();
    const { loading, setLoading, setRole, setResponsables, setTitre } = useUserInfosContext();
    const [activeStep, setActiveStep] = useState(0);
    const [completed, setCompleted] = useState<{
        [k: number]: boolean;
    }>({});

    const [isModalOpen, setIsModalOpen] = useState(false);
    const { logout } = useAppContext();
    let initialUser = process.env.NODE_ENV !== 'production' ? initUtilisateurExample() : initUtilisateur();
    initialUser.role = mode === 'newUser' ? TypeRole.ADMIN : TypeRole.UTILISATEUR;

    const steps = formStepListByMode(mode);

    const totalSteps = () => {
        return steps.length;
    };

    const completedSteps = () => {
        return Object.keys(completed).length;
    };

    const isLastFormStep = () => {
        return activeStep === totalSteps() - 1; // last step is confirmation
    };

    const lastStep = () => {
        return totalSteps() - 1; // last step is confirmation
    };

    const allStepsCompleted = () => {
        return completedSteps() === totalSteps();
    };

    const validateStep = async (activeStep) => {
        const validate = (result) => {
            if (result) {
                const newCompleted = completed;
                newCompleted[activeStep] = true;
                setCompleted(newCompleted);
            } else {
                const newCompleted = { ...completed };
                newCompleted[activeStep] = false;
                setCompleted(newCompleted);
            }
        };

        let result = false;
        switch (activeStep) {
            case 0:
                if (mode === 'newUser') {
                    result = await trigger(['titre', 'nom', 'prenom', 'telephone', 'email', 'fonction', 'role', 'responsable', 'login', 'password', 'confirmation']);
                } else {
                    result = await trigger(['titre', 'nom', 'prenom', 'telephone', 'email', 'fonction', 'role', 'responsable', 'accesWeb', 'login']);
                }
                validate(result);
                break;
            case 1:
                result = await trigger(['perimetre']);
                validate(result);
                break;
            case 2:
                result = await trigger(['roles']);
                validate(result);
                break;
            case 3:
                result = await trigger(['codeGestionnaireMultiple']);

                validate(result);
                break;
            case 4:
                result = true; //await trigger(['alerte']);
                return result;
            default:
                throw Error('Step validation is missing');
        }

        return result;
    };

    const handleBack = async () => {
        const hiddenStep = mode === 'newUser' && activeStep === 3 ? 2 : 0;
        setActiveStep((prevActiveStep) => prevActiveStep - 1 - hiddenStep);
        scrollTop();
    };

    const handleStep = (step: number) => async () => {
        setActiveStep(step);
        scrollTop();
    };

    const scrollTop = () => {
        window.scrollTo(0, 0);
    };

    const handleNext = async () => {
        const isValid = await validateStep(activeStep);
        if (!isValid) return;
        const hiddenStep = mode === 'newUser' && activeStep === 0 ? 2 : 0;
        const newActiveStep =
            isLastFormStep() && !allStepsCompleted()
                ? // It's the last step, but not all steps have been completed,
                  // find the first step that has been completed
                  steps.findIndex((_step, i) => !(i in completed))
                : activeStep + 1 + hiddenStep;
        setActiveStep(newActiveStep);
        scrollTop();
    };

    const handleComplete = async () => {
        const newCompleted = completed;
        newCompleted[activeStep] = true;
        setCompleted(newCompleted);
        delay(handleNext, 20);
    };

    const handleReset = () => {
        reset();
        handleFormCancel();
        setActiveStep(0);
        setCompleted({});
    };

    const handleResponse = (users: Utilisateur[]) => {
        // Set list of responsable + admin
        const responsables = responsablesFromUsers(users);
        setResponsables(responsables);
    };

    useEffect(() => {
        if (mode !== 'newUser') {
            setLoading(true);
            setLoadingPerimetre(true);
            utilisateurService
                .getUtilisateurs()
                .then((response: any) => handleResponse(response))
                .finally(() => setLoading(false));
        }
        if (!user.roles.includes('ROLE_NEW_INSCRIPTION')) {
            utilisateurService
                .getPerimetre()
                .then((response: any) => setPerimetre(response))
                .catch(() => setHasPerimetreErreur(true))
                .finally(() => setLoadingPerimetre(false));
        }

        // Get user connected data (details + rights)
        if (!user.roles.includes('ROLE_NEW_INSCRIPTION'))
            utilisateurService.getUserConnected().then((response) => {
                setUserConnected(response);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const validationSchema = FormValidation(selectedUser, mode);
    const methods = useForm<UserFormData>({
        resolver: yupResolver(validationSchema),
        defaultValues: selectedUser,
    });

    //* UseForm reset
    useEffect(
        () => {
            reset({ ...selectedUser });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedUser],
    );

    //* UseForm method destructuration
    const {
        handleSubmit,
        reset,
        setValue,
        getValues,
        control,
        trigger,
        watch,
        setError,
        formState: { errors, isDirty },
    } = methods;

    const { fields: fieldsCodeGestionnaire, append, remove } = useFieldArray({ name: 'codeGestionnaireMultiple', control });

    const initFormControlled = (initialData: UserFormData) => {
        setTitre(initialData.titre as TypeTitre);
        setRole(initialData.role as TypeRole);
        // Perimetre
        setSante(initialData.perimetre.risques.sante);
        setPrevoyance(initialData.perimetre.risques.prevoyance);
        setSelectedColleges(initialData.perimetre.colleges);
        setSelectedSouscrips(initialData.perimetre.souscripteurs);
        // init useForm
        setSelectedUser({ ...initialData });
    };

    useEffect(() => {
        initFormControlled({ ...initialUser });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFormCancel = () => {
        initFormControlled({ ...initialUser });
    };

    const handleServerSideErrors = (error) => {
        addServerErrors(error.response.data.formErrors, setError);
    };

    const handleSendForm = async (formData: UserFormData) => {
        setIsFormSubmitted(true);
        // Set last step as validated
        let newCompleted = { ...completed };
        newCompleted[activeStep - 2] = true;
        setCompleted(newCompleted);

        // Convert perimeter data
        let risques = [];
        if (formData.perimetre.risques.sante) risques.push('S');
        if (formData.perimetre.risques.prevoyance) risques.push('P');

        const souscripIds = formData.perimetre.souscripteurs.map((d) => d.value);
        const collegeIds = formData.perimetre.colleges.map((d) => d.value);

        const payload: Utilisateur = {
            id: formData.id,
            nom: formData.nom,
            prenom: formData.prenom,
            titre: formData.titre,
            telephone: formData.telephone,
            email: formData.email,
            fonction: formData.fonction,
            role: formData.role as TypeRole,
            responsable: formData.responsable,
            accesWeb: formData.accesWeb === 'true',
            login: formData.login,
            codeGestionnaire: formData.codeGestionnaire,
            codeGestionnaireMultiple: formData.codeGestionnaireMultiple,
            perimetre: {
                risques: risques,
                souscripteurs: souscripIds,
                colleges: collegeIds,
                excludeVIP: formData.perimetre.excludeVIP
            },
            roles: formData.roles,
            alertBia: {
                id: formData.alertBia.id,
                actif: formData.alertBia.actif,
                frequence: formData.alertBia.frequence,
                lastNotification: formData.alertBia.lastNotification,
            },
        };

        if (formData.password) {
            payload.password = formData.password;
        }

        if (mode === 'newUser') {
            await utilisateurService
                .createNewAccount(payload)
                .then(async () => {
                    const allStepCompleted = {};
                    steps.forEach((_, i) => (allStepCompleted[i] = true));
                    setCompleted(allStepCompleted);
                    setIsFormSuccess(true);
                    setIsFormSubmitted(false);
                })
                .catch((error) => {
                    handleServerSideErrors(error);
                    setIsFormSubmitted(false);
                    setIsFormSubmitted(false);
                });
        } else {
            await utilisateurService
                .postUser(payload)
                .then(async () => {
                    const allStepCompleted = {};
                    steps.forEach((_, i) => (allStepCompleted[i] = true));
                    setCompleted(allStepCompleted);
                    setIsFormSuccess(true);
                    setIsFormSubmitted(false);
                })
                .catch((error) => {
                    handleServerSideErrors(error);
                    setIsFormSubmitted(false);
                });
        }
    };

    const handleUniqueLogin = () => {
        setIsModalOpen(false);
    };

    const logoutAndReload = () => {
        logout();
        window.location.reload();
    };

    console.log(activeStep, lastStep());

    return (
        <>
            {isVisitorBackOffice(user) && (
                <Container style={{ width: '100%', height: '100%' }}>
                    <Card style={{ width: '100%', height: '100%' }}>
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                justifyContent: 'center' /* Center horizontally */,
                                alignItems: 'center' /* Center vertically */,
                            }}
                        >
                            <Box textAlign="center" className={styles.SGSEncartBlue}>
                                Vous ne disposez pas des droits pour ajouter un utilisateur
                            </Box>
                        </Box>
                    </Card>
                </Container>
            )}
            {!isVisitorBackOffice(user) && (
                <Container>
                    <Card>
                        <Box sx={{ width: '100%', height: '100%' }}>
                            <Stepper activeStep={activeStep} alternativeLabel>
                                {steps.map((label, index) => (
                                    <Step key={label} completed={completed[index]} className={styles.step}>
                                        <StepButton color="inherit" onClick={handleStep(index)}>
                                            <Typography className={styles.SGSStep}>{label}</Typography>
                                        </StepButton>
                                    </Step>
                                ))}
                            </Stepper>
                            <div>
                                {allStepsCompleted() ? (
                                    <React.Fragment>
                                        {isFormSuccess ? (
                                            <>
                                                {mode === 'newUser' && (
                                                    <>
                                                        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }} className="mb-5">
                                                            <Box sx={{ flex: '1 1 auto' }} />
                                                            <img src={reconnect} height={'249'} width={'auto'} alt="" />
                                                            <Box sx={{ flex: '1 1 auto' }} />
                                                        </Box>
                                                        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2, mb: 5 }}>
                                                            <Box sx={{ flex: '1 1 auto' }} />
                                                            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                                                                <img src={checkGreen} height={'40'} width={'auto'} alt="" />
                                                                <Typography style={{ color: '#46BE81', fontSize: '1.5rem', paddingLeft: '20px' }}>Votre compte a été créé avec succès</Typography>
                                                            </Box>
                                                            <Box sx={{ flex: '1 1 auto' }} />
                                                        </Box>

                                                        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2, mb: 5 }}>
                                                            <Box sx={{ flex: '1 1 auto' }} />
                                                            <span onClick={() => logoutAndReload()} className="pointer">
                                                                <img src={btnReconnect} height={'50'} width={'auto'} alt="" />
                                                            </span>
                                                            <Box sx={{ flex: '1 1 auto' }} />
                                                        </Box>
                                                    </>
                                                )}
                                                {mode !== 'newUser' && (
                                                    <>
                                                        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }} className="mb-5">
                                                            <Box sx={{ flex: '1 1 auto' }} />
                                                            <img src={accountCreated} height={'249'} width={'auto'} alt="" />
                                                            <Box sx={{ flex: '1 1 auto' }} />
                                                        </Box>
                                                        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2, mb: 5 }}>
                                                            <Box sx={{ flex: '1 1 auto' }} />
                                                            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                                                                <img src={checkGreen} height={'40'} width={'auto'} alt="" />
                                                                <Typography style={{ color: '#46BE81', fontSize: '1.5rem', paddingLeft: '20px' }}>Le compte a été créé avec succès</Typography>
                                                            </Box>
                                                            <Box sx={{ flex: '1 1 auto' }} />
                                                        </Box>
                                                        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2, mb: 5 }}>
                                                            <Box sx={{ flex: '1 1 auto' }} />
                                                            <SGSButton onClick={handleReset}>Ajouter un nouvel utilisateur</SGSButton>
                                                            <Box sx={{ flex: '1 1 auto' }} />
                                                        </Box>
                                                    </>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <Typography>Erreur !!!!</Typography>
                                            </>
                                        )}
                                    </React.Fragment>
                                ) : (
                                    <React.Fragment>
                                        {activeStep === 0 && (
                                            <FormProvider {...methods}>
                                                <SGSForm.Card onSubmit={handleSubmit(handleSendForm)} shadow={false}>
                                                    <FormInfos watch={watch} setValue={setValue} getValues={getValues} mode={mode} trigger={trigger} />
                                                </SGSForm.Card>
                                            </FormProvider>
                                        )}
                                        {activeStep === 1 && mode !== 'newUser' && (
                                            <FormProvider {...methods}>
                                                <SGSForm.Card onSubmit={handleSubmit(handleSendForm)} shadow={false}>
                                                    <FormPerimetre setValue={setValue} errors={errors} trigger={trigger}></FormPerimetre>
                                                </SGSForm.Card>
                                            </FormProvider>
                                        )}
                                        {activeStep === 2 && mode !== 'newUser' && (
                                            <FormProvider {...methods}>
                                                <SGSForm.Card onSubmit={handleSubmit(handleSendForm)} shadow={false}>
                                                    <FormDroits errors={errors} trigger={trigger} setValue={setValue} getValues={getValues}></FormDroits>
                                                </SGSForm.Card>
                                            </FormProvider>
                                        )}
                                        {activeStep === 3 && (
                                            <FormProvider {...methods}>
                                                <SGSForm.Card onSubmit={handleSubmit(handleSendForm)} shadow={false}>
                                                    <FormCodeGestionnaire
                                                        fields={fieldsCodeGestionnaire}
                                                        append={append}
                                                        remove={remove}
                                                        getValues={getValues}
                                                        watch={watch}
                                                        trigger={trigger}
                                                        errors={errors}
                                                    />
                                                </SGSForm.Card>
                                            </FormProvider>
                                        )}
                                        {activeStep === 4 && (
                                            <FormProvider {...methods}>
                                                <SGSForm.Card onSubmit={handleSubmit(handleSendForm)} shadow={false}>
                                                    <FormAlerte lastNotification={selectedUser.alertBia.lastNotification} mode={mode} />
                                                </SGSForm.Card>
                                            </FormProvider>
                                        )}

                                        {!isLastFormStep() && (
                                            <FormProvider {...methods}>
                                                <SGSForm.Card onSubmit={handleSubmit(handleSendForm)} shadow={false}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }} className="mb-4">
                                                        <Box sx={{ flex: '1 1 auto' }} />
                                                        <Box style={{ width: '250px' }}>
                                                            <SGSButton disabled={activeStep === 0} onClick={handleBack}>
                                                                Précédent
                                                            </SGSButton>
                                                        </Box>

                                                        <Box className="text-center " style={{ width: '250px' }}>
                                                            <SGSButton type="button" onClick={() => handleFormCancel()} disabled={!isDirty ? true : false}>
                                                                {t('global.cancel')}
                                                            </SGSButton>
                                                        </Box>

                                                        {((activeStep !== lastStep() - 1 && mode !== 'newUser') || (mode === 'newUser' && activeStep !== 3)) && (
                                                            <Box className="text-right" style={{ width: '250px' }}>
                                                                <SGSButton type="button" onClick={handleComplete} disabled={isCheckingLogin || isCheckingEmail}>
                                                                    Suivant
                                                                </SGSButton>
                                                            </Box>
                                                        )}

                                                        {(activeStep === lastStep() - 1 || (mode === 'newUser' && activeStep === 3)) && (
                                                            <Box className="text-right " style={{ width: '250px' }}>
                                                                <SGSButton type="submit" loading={isFormSubmitted}>
                                                                    {t('global.save')}
                                                                </SGSButton>
                                                            </Box>
                                                        )}

                                                        <Box sx={{ flex: '1 1 auto' }} />
                                                    </Box>
                                                </SGSForm.Card>
                                            </FormProvider>
                                        )}
                                    </React.Fragment>
                                )}
                            </div>
                        </Box>
                    </Card>

                    {loading && (
                        <Box className={styles.loading}>
                            <Loader loading={loading} />
                        </Box>
                    )}

                    <Modal setIsOpenModal={handleUniqueLogin} title={'Validation de votre identifiant'} isOpenModal={isModalOpen}>
                        <div className={'text-center'}>Veuillez patienter pendant la validation de votre identifiant</div>
                    </Modal>
                </Container>
            )}
        </>
    );
};

export default UserPageAdd;
